import { EventEmitter } from 'events';
import { Task, AnalysisResult, Recommendation } from '../types';
import { TaskAnalyzer } from './task-analyzer';
import { ContextEngine, Context, ContextType } from './context';
import { KnowledgeBase } from './knowledge-base';
import { logger } from '../utils/logger';

// 增强的分析结果
export interface EnhancedAnalysisResult extends AnalysisResult {
  context?: Context;
  suggestedPrompt?: string;
  relatedKnowledge?: Array<{
    title: string;
    relevance: number;
    summary: string;
  }>;
  workflowRecommendation?: {
    type: string;
    reason: string;
    steps: string[];
  };
  complexity?: {
    score: number; // 0-100
    factors: string[];
    estimatedTime?: number; // 分钟
  };
}

// 任务类型识别
export enum TaskType {
  FEATURE = 'feature',
  BUGFIX = 'bugfix',
  REFACTOR = 'refactor',
  DOCUMENTATION = 'documentation',
  TESTING = 'testing',
  PERFORMANCE = 'performance',
  SECURITY = 'security',
  DEVOPS = 'devops',
  RESEARCH = 'research',
  REVIEW = 'review'
}

// 增强的任务分析器
export class EnhancedTaskAnalyzer extends EventEmitter {
  private baseAnalyzer: TaskAnalyzer;
  
  constructor(
    private contextEngine: ContextEngine,
    private knowledgeBase: KnowledgeBase,
    options: {
      enableAI?: boolean;
      apiKey?: string;
    } = {}
  ) {
    super();
    this.baseAnalyzer = new TaskAnalyzer(options);
  }
  
  /**
   * 分析任务（增强版）
   */
  async analyze(input: string | Task): Promise<EnhancedAnalysisResult> {
    try {
      let task: Task;
      let baseResult: AnalysisResult;
      
      if (typeof input === 'string') {
        // 使用基础分析器解析任务
        baseResult = await this.baseAnalyzer.analyze(input);
        task = baseResult.tasks[0] || this.createTaskFromString(input);
      } else {
        task = input;
        baseResult = await this.baseAnalyzer.analyze(task.content);
      }
      
      // 识别任务类型
      const taskType = this.identifyTaskType(task);
      
      // 分析复杂度
      const complexity = this.analyzeComplexity(task, taskType);
      
      // 构建上下文
      const context = await this.buildTaskContext(task, taskType);
      
      // 搜索相关知识
      const relatedKnowledge = await this.searchRelatedKnowledge(task);
      
      // 生成工作流推荐
      const workflowRecommendation = await this.recommendWorkflow(task, taskType, complexity);
      
      // 生成 AI 提示词
      const suggestedPrompt = this.generatePrompt(task, taskType, context);
      
      // 增强推荐
      const enhancedRecommendations = await this.enhanceRecommendations(
        baseResult.recommendations || [],
        task,
        taskType,
        complexity
      );
      
      const enhancedResult: EnhancedAnalysisResult = {
        ...baseResult,
        recommendations: enhancedRecommendations,
        context,
        suggestedPrompt,
        relatedKnowledge,
        workflowRecommendation,
        complexity
      };
      
      this.emit('analyzed', enhancedResult);
      return enhancedResult;
      
    } catch (error) {
      logger.error('Enhanced task analysis failed:', error);
      throw error;
    }
  }
  
  /**
   * 识别任务类型
   */
  identifyTaskType(task: Task): TaskType {
    const content = task.content.toLowerCase();
    const title = task.title?.toLowerCase() || '';
    const combined = `${title} ${content}`;
    
    // 关键词映射
    const typePatterns: Record<TaskType, RegExp[]> = {
      [TaskType.FEATURE]: [
        /implement|add|create|build|develop|feature|functionality/i,
        /new\s+(component|module|service|api|endpoint)/i
      ],
      [TaskType.BUGFIX]: [
        /fix|bug|issue|error|problem|broken|crash|fail/i,
        /not\s+work|doesn't\s+work|exception/i
      ],
      [TaskType.REFACTOR]: [
        /refactor|restructure|reorganize|improve|optimize\s+code/i,
        /clean\s*up|simplify|extract|split/i
      ],
      [TaskType.DOCUMENTATION]: [
        /document|docs|readme|guide|tutorial|explain/i,
        /write\s+(docs|documentation|guide)/i
      ],
      [TaskType.TESTING]: [
        /test|spec|coverage|unit|integration|e2e/i,
        /add\s+test|write\s+test|test\s+case/i
      ],
      [TaskType.PERFORMANCE]: [
        /performance|optimize|speed|slow|fast|efficient/i,
        /reduce\s+(time|memory|size)|improve\s+performance/i
      ],
      [TaskType.SECURITY]: [
        /security|vulnerability|auth|encrypt|permission|access/i,
        /secure|protect|validate|sanitize/i
      ],
      [TaskType.DEVOPS]: [
        /deploy|ci\/cd|pipeline|docker|kubernetes|infrastructure/i,
        /build\s+process|automation|monitoring/i
      ],
      [TaskType.RESEARCH]: [
        /research|investigate|explore|analyze|study|evaluate/i,
        /find\s+out|look\s+into|compare/i
      ],
      [TaskType.REVIEW]: [
        /review|check|verify|validate|inspect|audit/i,
        /code\s+review|pr\s+review|merge/i
      ]
    };
    
    // 计算每种类型的匹配分数
    const scores = new Map<TaskType, number>();
    
    for (const [type, patterns] of Object.entries(typePatterns)) {
      let score = 0;
      for (const pattern of patterns) {
        if (pattern.test(combined)) {
          score += 1;
        }
      }
      scores.set(type as TaskType, score);
    }
    
    // 找出最高分的类型
    let maxScore = 0;
    let identifiedType = TaskType.FEATURE; // 默认
    
    for (const [type, score] of scores) {
      if (score > maxScore) {
        maxScore = score;
        identifiedType = type;
      }
    }
    
    return identifiedType;
  }
  
  /**
   * 分析任务复杂度
   */
  analyzeComplexity(task: Task, type: TaskType): EnhancedAnalysisResult['complexity'] {
    const factors: string[] = [];
    let score = 0;
    
    // 基于任务类型的基础分数
    const typeScores: Record<TaskType, number> = {
      [TaskType.FEATURE]: 60,
      [TaskType.BUGFIX]: 40,
      [TaskType.REFACTOR]: 70,
      [TaskType.DOCUMENTATION]: 30,
      [TaskType.TESTING]: 50,
      [TaskType.PERFORMANCE]: 80,
      [TaskType.SECURITY]: 80,
      [TaskType.DEVOPS]: 70,
      [TaskType.RESEARCH]: 50,
      [TaskType.REVIEW]: 20
    };
    
    score = typeScores[type];
    factors.push(`Base complexity for ${type}: ${score}`);
    
    // 分析内容复杂度
    const content = task.content.toLowerCase();
    
    // 检查是否涉及多个系统/模块
    if (content.match(/multiple|several|various|all|entire|whole/i)) {
      score += 20;
      factors.push('Involves multiple components (+20)');
    }
    
    // 检查是否需要架构决策
    if (content.match(/architect|design|structure|pattern|approach/i)) {
      score += 15;
      factors.push('Requires architectural decisions (+15)');
    }
    
    // 检查是否需要外部集成
    if (content.match(/integrat|external|third.?party|api|service/i)) {
      score += 10;
      factors.push('Requires external integration (+10)');
    }
    
    // 检查是否有依赖
    if (content.match(/depend|require|need|after|before|block/i)) {
      score += 10;
      factors.push('Has dependencies (+10)');
    }
    
    // 基于优先级调整
    if (task.priority === 'high') {
      score = Math.min(score + 10, 100);
      factors.push('High priority task (+10)');
    }
    
    // 估算时间（基于复杂度分数）
    let estimatedTime: number;
    if (score < 30) {
      estimatedTime = 30; // 30分钟
    } else if (score < 50) {
      estimatedTime = 60; // 1小时
    } else if (score < 70) {
      estimatedTime = 180; // 3小时
    } else if (score < 85) {
      estimatedTime = 480; // 8小时（1天）
    } else {
      estimatedTime = 960; // 16小时（2天）
    }
    
    return {
      score: Math.min(score, 100),
      factors,
      estimatedTime
    };
  }
  
  /**
   * 构建任务上下文
   */
  private async buildTaskContext(task: Task, type: TaskType): Promise<Context> {
    return this.contextEngine.build({
      task: {
        ...task,
        type,
        description: task.content
      },
      query: task.content,
      metadata: {
        taskId: task.id,
        taskType: type,
        priority: task.priority,
        status: task.status
      }
    });
  }
  
  /**
   * 搜索相关知识
   */
  private async searchRelatedKnowledge(task: Task) {
    try {
      const results = await this.knowledgeBase.search(task.content, {
        limit: 5,
        threshold: 0.3
      });
      
      return results.map(item => ({
        title: item.title,
        relevance: (item as any).score || 0.5,
        summary: this.extractSummary(item.content)
      }));
    } catch (error) {
      logger.warn('Failed to search knowledge base:', error);
      return [];
    }
  }
  
  /**
   * 推荐工作流
   */
  private async recommendWorkflow(
    task: Task,
    type: TaskType,
    complexity: EnhancedAnalysisResult['complexity']
  ) {
    const workflowMap: Record<TaskType, { type: string; steps: string[] }> = {
      [TaskType.FEATURE]: {
        type: 'feature',
        steps: [
          '1. 理解需求和验收标准',
          '2. 设计技术方案',
          '3. 实现核心功能',
          '4. 编写单元测试',
          '5. 集成测试',
          '6. 代码审查',
          '7. 文档更新'
        ]
      },
      [TaskType.BUGFIX]: {
        type: 'bugfix',
        steps: [
          '1. 复现问题',
          '2. 定位根因',
          '3. 设计修复方案',
          '4. 实现修复',
          '5. 验证修复',
          '6. 回归测试',
          '7. 部署验证'
        ]
      },
      [TaskType.REFACTOR]: {
        type: 'refactor',
        steps: [
          '1. 分析现有代码',
          '2. 识别改进点',
          '3. 设计重构方案',
          '4. 编写测试保护',
          '5. 逐步重构',
          '6. 验证功能不变',
          '7. 性能对比'
        ]
      },
      // ... 其他类型的工作流
    };
    
    const workflow = workflowMap[type] || {
      type: 'generic',
      steps: [
        '1. 分析任务需求',
        '2. 制定实施计划',
        '3. 执行实施',
        '4. 测试验证',
        '5. 文档记录'
      ]
    };
    
    // 根据复杂度调整步骤
    if (complexity && complexity.score > 70) {
      workflow.steps.unshift('0. 团队讨论和方案评审');
      workflow.steps.push(`${workflow.steps.length + 1}. 知识沉淀和经验总结`);
    }
    
    return {
      type: workflow.type,
      reason: `Based on task type (${type}) and complexity (${complexity?.score || 'N/A'})`,
      steps: workflow.steps
    };
  }
  
  /**
   * 生成 AI 提示词
   */
  private generatePrompt(task: Task, type: TaskType, context: Context): string {
    const prompts: Record<TaskType, string> = {
      [TaskType.FEATURE]: `Help me implement a new feature: ${task.content}

Please provide:
1. Technical design and architecture
2. Implementation steps
3. Key considerations and best practices
4. Potential edge cases
5. Testing strategy`,
      
      [TaskType.BUGFIX]: `Help me fix this bug: ${task.content}

Please help me:
1. Understand possible root causes
2. Debugging approach
3. Fix implementation
4. Verify the fix won't cause regressions
5. Prevent similar issues in the future`,
      
      [TaskType.REFACTOR]: `Help me refactor: ${task.content}

Please suggest:
1. Refactoring strategy
2. Step-by-step approach to maintain functionality
3. Design patterns to apply
4. Performance implications
5. Testing approach during refactoring`,
      
      // ... 其他类型的提示词模板
    };
    
    let prompt = prompts[type] || `Help me with this task: ${task.content}`;
    
    // 添加上下文信息
    if (context.items.length > 0) {
      const relevantContext = context.items
        .filter(item => item.type === ContextType.KNOWLEDGE || item.type === ContextType.WORKFLOW)
        .slice(0, 3)
        .map(item => {
          if (item.type === ContextType.KNOWLEDGE) {
            return `Related knowledge: ${item.content.title}`;
          }
          if (item.type === ContextType.WORKFLOW) {
            return `Suggested workflow: ${item.content.name}`;
          }
          return '';
        })
        .filter(Boolean)
        .join('\n');
      
      if (relevantContext) {
        prompt += `\n\nContext:\n${relevantContext}`;
      }
    }
    
    return prompt;
  }
  
  /**
   * 增强推荐
   */
  private async enhanceRecommendations(
    baseRecommendations: Recommendation[],
    task: Task,
    type: TaskType,
    complexity?: EnhancedAnalysisResult['complexity']
  ): Promise<Recommendation[]> {
    const enhanced = [...baseRecommendations];
    
    // 添加基于任务类型的推荐
    if (type === TaskType.FEATURE && complexity && complexity.score > 60) {
      enhanced.unshift({
        type: 'workflow',
        priority: 'high',
        title: 'Create design document first',
        description: 'This feature is complex. Consider creating a design document before implementation.',
        action: 'Create a technical design document outlining the approach'
      });
    }
    
    if (type === TaskType.BUGFIX) {
      enhanced.push({
        type: 'improvement',
        priority: 'medium',
        title: 'Add regression test',
        description: 'After fixing the bug, add a test to prevent regression',
        action: 'Write unit/integration test for the bug fix'
      });
    }
    
    if (type === TaskType.PERFORMANCE) {
      enhanced.push({
        type: 'improvement',
        priority: 'high',
        title: 'Benchmark before optimization',
        description: 'Measure current performance before making changes',
        action: 'Create performance benchmarks'
      });
    }
    
    // 基于复杂度的推荐
    if (complexity && complexity.estimatedTime && complexity.estimatedTime > 240) {
      enhanced.push({
        type: 'workflow',
        priority: 'medium',
        title: 'Break down into subtasks',
        description: `This task is estimated to take ${Math.round(complexity.estimatedTime / 60)} hours. Consider breaking it down.`,
        action: 'Create subtasks for better tracking and progress'
      });
    }
    
    return enhanced;
  }
  
  /**
   * 创建任务对象
   */
  private createTaskFromString(content: string): Task {
    return {
      id: Date.now().toString(),
      content,
      status: 'pending',
      priority: 'medium',
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }
  
  /**
   * 提取摘要
   */
  private extractSummary(content: string, maxLength: number = 150): string {
    const cleaned = content
      .replace(/```[\s\S]*?```/g, '') // 移除代码块
      .replace(/\n+/g, ' ') // 替换换行
      .trim();
    
    if (cleaned.length <= maxLength) {
      return cleaned;
    }
    
    return cleaned.substring(0, maxLength - 3) + '...';
  }
  
  /**
   * 批量分析任务
   */
  async analyzeMultiple(tasks: Task[]): Promise<EnhancedAnalysisResult[]> {
    const results: EnhancedAnalysisResult[] = [];
    
    // 分批处理，避免过载
    const batchSize = 5;
    for (let i = 0; i < tasks.length; i += batchSize) {
      const batch = tasks.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(task => this.analyze(task))
      );
      results.push(...batchResults);
    }
    
    return results;
  }
}